import React, { createContext, useContext, useState } from 'react';

export const light = {
  pageBg:          '#f9f9fb',
  card:            '#ffffff',
  nav:             'rgba(255,255,255,0.85)',
  navBorder:       'rgba(191,199,212,0.3)',
  navActive:       '#0061a4',
  border:          '#e2e4e8',
  borderLight:     '#eceef1',
  borderMid:       '#e2e4e8',
  borderFaint:     'rgba(150,160,175,0.5)',
  inputBg:         '#ffffff',
  tableHeader:     '#f3f3f5',
  tableSubHeader:  '#eeeef0',
  tableInputRow:   '#f9f9fb',
  tableRowAlt:     '#fafafa',
  tableRowHover:   '#f3f3f5',
  text:            '#1a1c1d',
  textSub:         '#1a1c1d',
  textMid:         '#404752',
  textSecondary:   '#404752',
  textTertiary:    '#707883',
  textMuted:       '#707883',
  textFaint:       '#bfc7d4',
  textFaintest:    '#d9dadc',
  overlay:         'rgba(0,0,0,0.45)',
  shadow:          '0 1px 4px rgba(0,0,0,0.08)',
  shadowMed:       '0 12px 32px -4px rgba(26,28,29,0.08)',
  shadowLarge:     '0 8px 30px rgba(0,0,0,0.14)',
  shadowXL:        '0 20px 60px rgba(0,0,0,0.2)',
};

export const dark = {
  // Surfaces — neutral slate, clearly layered (inspired by GitHub Dark / Linear)
  pageBg:          '#0d1117',
  card:            '#161b22',
  nav:             '#161b22',
  navBorder:       '#30363d',
  navActive:       '#58a6ff',
  border:          '#444c56',
  borderLight:     '#2d333b',
  borderMid:       '#3d444d',
  borderFaint:     '#444c56',
  inputBg:         '#0d1117',
  tableHeader:     '#1c2128',
  tableSubHeader:  '#21262d',
  tableInputRow:   '#161b22',
  tableRowAlt:     '#0d1117',
  tableRowHover:   '#1c2128',
  // Text — bright and clearly tiered so nothing blends into the surface
  text:            '#f0f6fc',
  textSub:         '#e6edf3',
  textMid:         '#adbac7',
  textSecondary:   '#adbac7',
  textTertiary:    '#768390',
  textMuted:       '#8b949e',
  textFaint:       '#6e7681',
  textFaintest:    '#484f58',
  overlay:         'rgba(0,0,0,0.65)',
  shadow:          '0 1px 4px rgba(0,0,0,0.3)',
  shadowMed:       '0 4px 24px rgba(0,0,0,0.4)',
  shadowLarge:     '0 8px 30px rgba(0,0,0,0.5)',
  shadowXL:        '0 20px 60px rgba(0,0,0,0.6)',
};

const ThemeContext = createContext({ isDark: false, toggle: () => {}, t: light, bigText: false, setBigText: () => {}, highContrast: false, setHighContrast: () => {}, friendlyMode: true, setFriendlyMode: () => {} });

export function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem('bpp_theme') === 'dark'; } catch { return false; }
  });
  const [bigText, setBigTextState] = useState(() => {
    try { return localStorage.getItem('bpp_bigText') === 'true'; } catch { return false; }
  });
  const [highContrast, setHighContrastState] = useState(() => {
    try { return localStorage.getItem('bpp_highContrast') === 'true'; } catch { return false; }
  });
  const [friendlyMode, setFriendlyModeState] = useState(() => {
    try { return localStorage.getItem('bpp_friendlyMode') !== 'false'; } catch { return true; }
  });

  const toggle = () => setIsDark((d) => {
    const next = !d;
    try { localStorage.setItem('bpp_theme', next ? 'dark' : 'light'); } catch {}
    return next;
  });

  const setBigText = (val) => {
    setBigTextState(val);
    try { localStorage.setItem('bpp_bigText', val ? 'true' : 'false'); } catch {}
  };

  const setHighContrast = (val) => {
    setHighContrastState(val);
    try { localStorage.setItem('bpp_highContrast', val ? 'true' : 'false'); } catch {}
  };

  const setFriendlyMode = (val) => {
    setFriendlyModeState(val);
    try { localStorage.setItem('bpp_friendlyMode', val ? 'true' : 'false'); } catch {}
  };

  let tokens = isDark ? dark : light;
  if (highContrast) {
    tokens = isDark
      ? { ...tokens, text: '#ffffff', textSub: '#ffffff', textMid: '#eeeeee', textSecondary: '#dddddd', textTertiary: '#cccccc', textMuted: '#bbbbbb', pageBg: '#000000', card: '#0a0a0a', border: '#666666', borderLight: '#555555', borderMid: '#606060' }
      : { ...tokens, text: '#000000', textSub: '#000000', textMid: '#111111', textSecondary: '#111111', textTertiary: '#222222', textMuted: '#333333', card: '#ffffff', pageBg: '#e8e8e8', border: '#444444', borderLight: '#555555', borderMid: '#444444' };
  }

  return (
    <ThemeContext.Provider value={{ isDark, toggle, t: tokens, bigText, setBigText, highContrast, setHighContrast, friendlyMode, setFriendlyMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
